'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';

/**
 * "Draw all" in the book panel: every missing reference at once — heroes AND props.
 *
 * Each one is prepared exactly like a single "draw" (reserve, character row, 'generating'),
 * then the secondary characters and the props go onto reference sheets (refSheets.ts) the
 * way a new book draws them, and the rest get their own portrait. Everything is enqueued,
 * visible in the panel and billed like a portrait — one image per sheet render.
 */
export async function generateMissingReferences(input: {
  bookId: string;
  names: string[];
}): Promise<ActionResult<{ portraits: number; sheets: number }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { CharacterReferenceService } = await import('@/contexts/illustrations/services/CharacterReferenceService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { getImageGenerationQueue } = await import('@/contexts/ai/server');
    const { buildSheetJobs, storedTiers } = await import('@/contexts/illustrations/services/refSheets.server');
    const { refSheetsEnabled } = await import('@/contexts/illustrations/services/refSheets');

    const repo = getIllustrationRepository();
    const service = new CharacterReferenceService(repo, getCharacterRepository(), getTokenManager());
    // OUTFIT VARIANTS («Варя — пижама», wardrobeRefs.ts) are edits of their canonical references
    const current = await repo.getById(input.bookId, familyId);
    const variants = new Set((current?.characterReferences ?? []).filter((r) => !!r.variantOf).map((r) => r.extractedName));
    const asked = [...new Set(input.names)];
    let variantJobs = 0;
    if (asked.some((n) => variants.has(n))) {
      const { orderWardrobeVariants } = await import('@/contexts/illustrations/services/wardrobeRefs.server');
      const v = await orderWardrobeVariants({ bookId: input.bookId, familyId, redraw: asked.filter((n) => variants.has(n)) });
      variantJobs = v.ordered.length;
    }
    const collected: Record<string, unknown>[] = [];
    for (const name of asked.filter((n) => !variants.has(n)).slice(0, 24)) {
      await service.generateReference({
        bookId: input.bookId,
        familyId,
        extractedName: name,
        createdBy: userId,
        collectPayload: (payload) => collected.push(payload),
      });
    }
    if (!collected.length) return { success: true, data: { portraits: variantJobs, sheets: 0 } };

    const book = await repo.getById(input.bookId, familyId);
    let jobs = collected;
    let sheets = 0;
    if (book && refSheetsEnabled()) {
      const plan = await buildSheetJobs({
        book,
        familyId,
        characters: (book.storyAnalysis?.characters ?? []) as never,
        portraitJobs: collected,
        tiers: storedTiers(book),
        refService: service,
      });
      jobs = plan.jobs;
      sheets = plan.sheets;
    }
    const queue = getImageGenerationQueue();
    for (const job of jobs) await queue.enqueue(job as never);
    return {
      success: true,
      data: { portraits: jobs.filter((j) => j.type === 'character-portrait').length + variantJobs, sheets },
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('generateMissingReferences', e) };
  }
}
